"use client";

import * as Sentry from "@sentry/capacitor";
import { isSentryEnabled } from "@/lib/sentry";
import {
  asRecord,
  describeStructuredError,
  sanitizeStructuredRecord,
  truncateNonEmptyText,
} from "@/lib/structuredValue";

export type ProviderClientIssueProvider =
  | "spotify"
  | "youtube"
  | "applemusic";

export type ProviderClientIssueCategory = "auth" | "playback";

export type ProviderClientIssueLevel = "warn" | "error";

export type ProviderClientIssueEntry = {
  seq: number;
  timestamp: string;
  provider: ProviderClientIssueProvider;
  category: ProviderClientIssueCategory;
  level: ProviderClientIssueLevel;
  event: string;
  title: string;
  message: string | null;
  persist: boolean;
  details: Record<string, unknown>;
};

type ProviderClientIssueTagValue =
  | string
  | number
  | boolean
  | null
  | undefined;

const PROVIDER_CLIENT_ISSUE_BUFFER_LIMIT = 200;

export const PROVIDER_CLIENT_ISSUE_EVENT = "imags:provider-client-issue";

let fallbackProviderClientIssueSequence = 0;

declare global {
  interface Window {
    __IMAGS_PROVIDER_CLIENT_ISSUES__?: ProviderClientIssueEntry[];
    __IMAGS_PROVIDER_CLIENT_ISSUE_SEQ__?: number;
  }
}

function nextProviderClientIssueSequence() {
  if (typeof window === "undefined") {
    fallbackProviderClientIssueSequence += 1;
    return fallbackProviderClientIssueSequence;
  }

  const next =
    typeof window.__IMAGS_PROVIDER_CLIENT_ISSUE_SEQ__ === "number"
      ? window.__IMAGS_PROVIDER_CLIENT_ISSUE_SEQ__ + 1
      : fallbackProviderClientIssueSequence + 1;
  window.__IMAGS_PROVIDER_CLIENT_ISSUE_SEQ__ = next;
  fallbackProviderClientIssueSequence = next;
  return next;
}

function sanitizeProviderClientIssueDetails(
  details: Record<string, unknown>
): Record<string, unknown> {
  return sanitizeStructuredRecord(details);
}

function describeProviderClientIssueError(error: unknown) {
  const record = asRecord(error);
  return describeStructuredError(error, {
    fallbackMessage: "Unknown client issue",
    nameMaxLength: 120,
    codeMaxLength: 120,
    stackMaxLength: 1200,
    data: record.data ?? record.details ?? record.response ?? null,
  });
}

function emitProviderClientIssue(entry: ProviderClientIssueEntry) {
  if (typeof window === "undefined") {
    return entry;
  }

  const buffer = Array.isArray(window.__IMAGS_PROVIDER_CLIENT_ISSUES__)
    ? window.__IMAGS_PROVIDER_CLIENT_ISSUES__
    : [];
  buffer.push(entry);
  if (buffer.length > PROVIDER_CLIENT_ISSUE_BUFFER_LIMIT) {
    buffer.splice(0, buffer.length - PROVIDER_CLIENT_ISSUE_BUFFER_LIMIT);
  }
  window.__IMAGS_PROVIDER_CLIENT_ISSUE_SEQ__ = entry.seq;
  window.__IMAGS_PROVIDER_CLIENT_ISSUES__ = buffer;
  window.dispatchEvent(
    new CustomEvent<ProviderClientIssueEntry>(PROVIDER_CLIENT_ISSUE_EVENT, {
      detail: entry,
    })
  );
  return entry;
}

function normalizeProviderClientIssueTagValue(
  value: ProviderClientIssueTagValue
) {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  return String(value);
}

export function appendProviderClientIssue({
  provider,
  category,
  level,
  event,
  title,
  message = null,
  persist = true,
  details = {},
}: {
  provider: ProviderClientIssueProvider;
  category: ProviderClientIssueCategory;
  level: ProviderClientIssueLevel;
  event: string;
  title: string;
  message?: string | null;
  persist?: boolean;
  details?: Record<string, unknown>;
}) {
  return emitProviderClientIssue({
    seq: nextProviderClientIssueSequence(),
    timestamp: new Date().toISOString(),
    provider,
    category,
    level,
    event,
    title,
    message: truncateNonEmptyText(message, 600),
    persist,
    details: sanitizeProviderClientIssueDetails(details),
  });
}

export function reportProviderClientIssue({
  provider,
  category,
  level,
  event,
  title,
  message = null,
  persist = true,
  error,
  details = {},
  tags = {},
  fingerprint,
}: {
  provider: ProviderClientIssueProvider;
  category: ProviderClientIssueCategory;
  level: ProviderClientIssueLevel;
  event: string;
  title: string;
  message?: string | null;
  persist?: boolean;
  error?: unknown;
  details?: Record<string, unknown>;
  tags?: Record<string, ProviderClientIssueTagValue>;
  fingerprint?: string[];
}) {
  const describedError =
    error !== undefined ? describeProviderClientIssueError(error) : null;
  const entry = appendProviderClientIssue({
    provider,
    category,
    level,
    event,
    title,
    message: message || describedError?.message || null,
    persist,
    details: {
      ...(details || {}),
      ...(describedError ? { error: describedError } : {}),
    },
  });

  if (!isSentryEnabled()) {
    return entry;
  }

  Sentry.withScope((scope) => {
    scope.setLevel(level === "error" ? "error" : "warning");
    scope.setTag("provider", provider);
    scope.setTag("client_issue_provider", provider);
    scope.setTag("client_issue_category", category);
    scope.setTag("client_issue_level", level);
    scope.setTag("client_issue_event", event);
    scope.setTag("client_issue_persist", String(persist));

    for (const [tagKey, tagValue] of Object.entries(tags)) {
      const normalizedValue = normalizeProviderClientIssueTagValue(tagValue);
      if (normalizedValue) {
        scope.setTag(tagKey, normalizedValue);
      }
    }

    scope.setContext("provider_issue", {
      provider,
      category,
      level,
      event,
      title,
      message: entry.message,
      persist,
    });

    if (Object.keys(entry.details).length > 0) {
      scope.setContext("provider_issue_details", entry.details);
    }

    if (Array.isArray(fingerprint) && fingerprint.length > 0) {
      scope.setFingerprint(fingerprint);
    }

    if (error instanceof Error) {
      Sentry.captureException(error);
      return;
    }

    if (describedError) {
      const synthesizedError = new Error(describedError.message);
      if (describedError.name) {
        synthesizedError.name = describedError.name;
      }
      Sentry.captureException(synthesizedError);
      return;
    }

    Sentry.captureMessage(
      entry.message ? `${title}: ${entry.message}` : title,
      level === "error" ? "error" : "warning"
    );
  });

  return entry;
}
